import type { ProposalKind } from "../api/types";
import type { Principal } from "../types";

export const ROLE = {
  admin: "fer:admin",
  callTaker: "fer:call-taker",
  dispatcher: "fer:dispatcher",
  crewCommander: "fer:crew-commander",
  fireOfficer: "fer:fire-officer",
  chief: "fer:chief",
  cityManager: "hub:city-manager",
  financeOfficer: "hub:finance-officer",
} as const;

const {
  admin,
  callTaker: ct,
  dispatcher: disp,
  crewCommander: cc,
  fireOfficer: fo,
  chief,
  cityManager: cm,
  financeOfficer: fin,
} = ROLE;

/** FER's own staff roles (a FER local token carries only these). */
export const FER_ROLES: readonly string[] = [admin, ct, disp, cc, fo, chief];

/**
 * The audience of this staff module. Citizens (`self`, SOS and owners' applications) use the Smart City app, crews
 * and fire officers capture their field work in the Smart City Work app, and `hub:service` is a machine client, so
 * those operations are never offered here even where the contract allows them.
 */
export const STAFF_ROLES: readonly string[] = [...FER_ROLES, cm, fin];

/** Operations whose contract `x-roles` is `signed-in`: every staff role of this module may call them. */
const SIGNED_IN = STAFF_ROLES;
const ALL_FER = FER_ROLES;

/**
 * UI actions and the contract operation that backs each. `roles` are the operation's `x-roles` limited to the staff
 * roles above (`signed-in` = all of them); `local` is its `x-local-auth` (a FER local session may call it).
 * tests/contract-sync.test.ts checks every entry against ../contracts/openapi.yaml. The server still enforces role,
 * ward and station scope, four-eyes and conflict of interest; hiding an action only avoids dead ends.
 */
export const ACTIONS = {
  viewConfig: { operationId: "getFireConfig", roles: SIGNED_IN, local: true },
  // intake
  viewAlerts: { operationId: "listAlerts", roles: [ct, disp, chief], local: true },
  createAlert: { operationId: "createAlert", roles: [ct, disp], local: true },
  viewAlert: { operationId: "getAlert", roles: [ct, disp, chief], local: true },
  claimAlert: { operationId: "claimAlert", roles: [ct, disp], local: true },
  setAlertLocation: { operationId: "setAlertLocation", roles: [ct, disp], local: true },
  sendLocationLink: { operationId: "sendLocationLink", roles: [ct, disp], local: true },
  recordCallback: { operationId: "recordCallbackAttempt", roles: [ct, disp], local: true },
  confirmAlert: { operationId: "confirmAlert", roles: [ct, disp], local: true },
  linkAlert: { operationId: "linkAlert", roles: [ct, disp], local: true },
  dismissAlert: { operationId: "dismissAlert", roles: [ct, disp, chief], local: true },
  reopenAlert: { operationId: "reopenAlert", roles: [ct, disp, chief], local: true },
  viewRefusedSos: { operationId: "listRefusedSos", roles: [ct, disp, chief], local: true },
  // maps
  searchPlaces: { operationId: "searchPlaces", roles: ALL_FER, local: true },
  lookupWard: { operationId: "lookupWard", roles: ALL_FER, local: true },
  viewWardMap: { operationId: "getWardMap", roles: ALL_FER, local: true },
  viewLiveMap: { operationId: "getLiveMap", roles: [ct, disp, chief, admin], local: true },
  // incidents and dispatch
  viewIncidents: { operationId: "listIncidents", roles: [ct, disp, chief, admin, fo, cc], local: true },
  viewIncident: { operationId: "getIncident", roles: [ct, disp, chief, admin, fo, cc], local: true },
  updateIncident: { operationId: "updateIncident", roles: [ct, disp], local: true },
  escalateIncident: { operationId: "escalateIncident", roles: [disp, cc], local: true },
  containIncident: { operationId: "markIncidentContained", roles: [disp, cc], local: true },
  cancelIncident: { operationId: "cancelIncident", roles: [disp, chief], local: true },
  viewSuggestions: { operationId: "getDispatchSuggestions", roles: [disp, ct, chief], local: true },
  assignAppliances: { operationId: "assignAppliances", roles: [disp], local: true },
  viewDispatch: { operationId: "getDispatch", roles: [disp, ct, chief, cc], local: true },
  recordDispatchStatus: { operationId: "recordDispatchStatus", roles: [disp, cc], local: true },
  standDown: { operationId: "standDownDispatch", roles: [disp], local: true },
  // incident reports
  viewReport: { operationId: "getIncidentReport", roles: [ct, disp, chief, admin, fo, cc], local: true },
  decideReport: { operationId: "decideIncidentReport", roles: [fo, chief], local: false },
  // resources
  viewStations: { operationId: "listStations", roles: ALL_FER, local: true },
  createStation: { operationId: "createStation", roles: [admin], local: false },
  viewStation: { operationId: "getStation", roles: ALL_FER, local: true },
  updateStation: { operationId: "updateStation", roles: [admin], local: false },
  viewAppliances: { operationId: "listAppliances", roles: [ct, disp, cc, chief, admin], local: true },
  createAppliance: { operationId: "createAppliance", roles: [admin], local: false },
  viewAppliance: { operationId: "getAppliance", roles: [ct, disp, cc, chief, admin], local: true },
  updateAppliance: { operationId: "updateAppliance", roles: [admin], local: false },
  setServiceStatus: { operationId: "setApplianceServiceStatus", roles: [admin, disp, cc], local: true },
  viewAssignments: { operationId: "listStationAssignments", roles: [admin, chief, cc], local: false },
  createAssignment: { operationId: "createStationAssignment", roles: [admin], local: false },
  endAssignment: { operationId: "endStationAssignment", roles: [admin], local: false },
  // prevention
  viewPremises: { operationId: "listPremises", roles: [fo, chief, admin], local: false },
  registerPremises: { operationId: "registerPremises", roles: [fo, admin], local: false },
  viewPremisesDetail: { operationId: "getPremises", roles: [fo, chief, admin], local: false },
  updatePremises: { operationId: "updatePremises", roles: [fo, admin], local: false },
  changeRiskClass: { operationId: "changePremisesRiskClass", roles: [fo, admin, chief], local: false },
  changeOccupancy: { operationId: "changePremisesOccupancy", roles: [fo, admin, chief], local: false },
  changeOwner: { operationId: "changePremisesOwner", roles: [fo, admin, chief], local: false },
  viewApplications: { operationId: "listCertificateApplications", roles: [fo, chief, admin, fin], local: false },
  applyForCertificate: { operationId: "applyForCertificate", roles: [fo], local: false },
  viewApplication: { operationId: "getCertificateApplication", roles: [fo, chief, admin, fin], local: false },
  checkoutFee: { operationId: "checkoutCertificateFee", roles: [fo], local: false },
  cancelBill: { operationId: "cancelCertificateBill", roles: [fo, admin, chief, fin], local: false },
  requestReinspection: { operationId: "requestReinspection", roles: [fo], local: false },
  viewInspections: { operationId: "listInspections", roles: [fo, chief, admin], local: false },
  viewInspection: { operationId: "getInspection", roles: [fo, chief, admin], local: false },
  viewHeldItems: { operationId: "listHeldItems", roles: [fo, chief], local: false },
  reviewHeldItem: { operationId: "reviewHeldItem", roles: [fo, chief], local: false },
  viewCertificates: { operationId: "listCertificates", roles: [fo, chief, admin], local: false },
  viewCertificate: { operationId: "getCertificate", roles: [fo, chief, admin], local: false },
  revokeCertificate: { operationId: "revokeCertificate", roles: [fo, chief], local: false },
  viewHydrants: { operationId: "listHydrants", roles: ALL_FER, local: true },
  registerHydrant: { operationId: "registerHydrant", roles: [fo, admin], local: false },
  viewHydrant: { operationId: "getHydrant", roles: ALL_FER, local: true },
  updateHydrant: { operationId: "updateHydrant", roles: [fo, admin], local: false },
  surveyHydrant: { operationId: "recordHydrantSurvey", roles: [fo], local: false },
  // files
  downloadFile: { operationId: "getFileDownload", roles: SIGNED_IN, local: true },
  // local sign-in credentials
  viewMyCredentials: { operationId: "listMyLocalCredentials", roles: ALL_FER, local: false },
  enrolCredential: { operationId: "enrolLocalCredential", roles: ALL_FER, local: false },
  removeMyCredential: { operationId: "deleteMyLocalCredential", roles: ALL_FER, local: false },
  viewAllCredentials: { operationId: "listLocalCredentials", roles: [admin], local: false },
  resetCredential: { operationId: "resetLocalCredential", roles: [admin], local: true },
  // approvals
  viewApprovals: { operationId: "listApprovals", roles: [fo, chief, admin, disp, fin], local: true },
  viewApproval: { operationId: "getApproval", roles: [fo, chief, admin, disp, fin], local: true },
  // reports
  viewResponseTimes: { operationId: "getResponseTimeReport", roles: [chief, admin, cm], local: false },
  viewSnapshot: { operationId: "getReportSnapshot", roles: [cm, chief, admin], local: false },
} as const satisfies Record<string, { operationId: string; roles: readonly string[]; local: boolean }>;

export type Action = keyof typeof ACTIONS;

/**
 * Where the console narrows an operation's roles (tests check each is a subset of the action's roles):
 * - crews record statuses, containment and escalation from the Work app (the console records the radio);
 * - under a FER local session, `resetLocalCredential` may only `unlock` (revoke needs a normal sign-in).
 */
export const NARROWED = {
  recordRadioStatus: { action: "recordDispatchStatus", roles: [disp] },
  escalateFromConsole: { action: "escalateIncident", roles: [disp] },
  containFromConsole: { action: "containIncident", roles: [disp] },
} as const satisfies Record<string, { action: Action; roles: readonly string[] }>;

/**
 * Four-eyes proposal kinds (`x-four-eyes` in the contract): who may propose, and who may confirm or reject. The
 * proposer may withdraw and is never offered confirm. tests/contract-sync.test.ts checks these against the contract.
 */
export const FOUR_EYES = {
  incident_cancellation: { operationId: "cancelIncident", propose: [disp], confirm: [chief, disp] },
  risk_class_change: { operationId: "changePremisesRiskClass", propose: [fo, admin], confirm: [fo, chief] },
  occupancy_change: { operationId: "changePremisesOccupancy", propose: [fo, admin], confirm: [fo, chief] },
  owner_change: { operationId: "changePremisesOwner", propose: [fo, admin], confirm: [fo, chief] },
  bill_cancellation: { operationId: "cancelCertificateBill", propose: [fo, admin], confirm: [chief, fin] },
  certificate_revocation: { operationId: "revokeCertificate", propose: [fo], confirm: [fo, chief] },
} as const satisfies Record<ProposalKind, { operationId: string; propose: readonly string[]; confirm: readonly string[] }>;

export type FourEyesKind = keyof typeof FOUR_EYES;
export type FourEyesStep = "propose" | "confirm";

/** A FER local session (the identity service was unreachable): only `x-local-auth` operations. */
export function isLocal(principal: Pick<Principal, "auth">): boolean {
  return principal.auth === "local_auth";
}

export function hasAnyRole(principal: Pick<Principal, "roles">, roles: readonly string[]): boolean {
  return roles.some((r) => principal.roles.includes(r));
}

export function can(principal: Pick<Principal, "roles" | "auth">, action: Action): boolean {
  const a = ACTIONS[action];
  if (isLocal(principal) && !a.local) return false;
  return hasAnyRole(principal, a.roles);
}

export function canNarrowed(principal: Pick<Principal, "roles" | "auth">, key: keyof typeof NARROWED): boolean {
  const n = NARROWED[key];
  return can(principal, n.action) && hasAnyRole(principal, n.roles);
}

/** Whether the principal may take a four-eyes step (UI only). Under a local session only incident cancellations. */
export function canStep(principal: Pick<Principal, "roles" | "auth">, kind: FourEyesKind, step: FourEyesStep): boolean {
  const rule = FOUR_EYES[kind];
  const action = (Object.keys(ACTIONS) as Action[]).find((k) => ACTIONS[k].operationId === rule.operationId);
  if (!action || !can(principal, action)) return false;
  return hasAnyRole(principal, rule[step]);
}

/** The control room and the chief see callers (masked in lists, in full on the alert). Never `fer:admin`. */
export function seesCallers(principal: Pick<Principal, "roles" | "auth">): boolean {
  return can(principal, "viewAlert");
}

/** Fire officers act only in their wards (`*` = all); everyone else here is city-wide or station scoped. */
export function wardScoped(principal: Pick<Principal, "roles">): boolean {
  return principal.roles.includes(fo) && !hasAnyRole(principal, [chief, admin, fin, cm]);
}

export function holdsWard(principal: Pick<Principal, "wardIds">, wardId: string | null | undefined): boolean {
  if (!wardId) return false;
  return principal.wardIds.includes("*") || principal.wardIds.includes(wardId);
}
